# Terms & Conditions

**Effective date: September 1, 2026**

These Terms & Conditions ("Terms") govern your access to and use of the {{companyName}} platform available at [{{domain}}]({{url}}) (the "Service"), operated by {{legalName}}, a Bulgarian variable-capital company (дружество с променлив капитал), UIC {{uic}}, VAT No. {{vatNumber}}, registered seat: {{registeredAddress}} ("we", "us", or "our").

By registering for or using the Service, you confirm that you have read, understood, and agree to be bound by these Terms. If you do not agree, you must not use the Service.

---

## 1. About the Service

{{companyName}} is a web-based platform for tracking properties and collaborating on them. Depending on the available functionality, the Service provides: submission of listing URLs with automatic data extraction, property records with every advertisement of a property attached, periodic re-checking of tracked listings and change detection (price, availability, and other fields), in-app and email alerts, property stages, a viewing and availability calendar with real calendar invitations sent by email, workspaces for collaboration with members invited by email or share link, organization property pools, custom fields, an activity feed, manual property entry as a first-class source, and image uploads (advert galleries are public; images uploaded by users are private to their organization or workspace).

Property data is collected from publicly accessible listing websites — at launch, imot.bg — and from manual entry. Additional sources may be added over time.

**Planned features** (such as additional source sites, push notifications, or paid plans) are not part of the Service until officially released. Announcing planned features in advance creates no obligation to deliver them by any specific date or at all.

The Service is intended for individuals tracking properties for personal purposes and for real-estate agencies and their teams.

**The Service is not a real-estate agency** and provides no brokerage, intermediary, escrow, or payment-processing services for the purchase, sale, or rental of properties. All decisions regarding a property are solely the responsibility of the user.

**Property and listing data may be inaccurate or outdated.** It is collected from third-party sources and we cannot guarantee its accuracy, completeness, or timeliness. Always verify information against the original listing and the property itself before acting on it.

---

## 2. Eligibility

To register and use the Service, you must:

- Be at least 16 years old
- Be an individual tracking properties for personal purposes, or act on behalf of a legally registered real-estate agency operating in a market the Service covers
- Provide accurate, complete, and truthful information during registration
- Have the legal authority to enter into these Terms on behalf of yourself or your organization

**Consumer protection.** If you use the Service exclusively for personal purposes outside any professional or commercial activity, consumer protection legislation applies to you. Where you contract within your professional or commercial activity (for example, as a real-estate agency), the consumer protection rules do not apply to that use.

We reserve the right to verify the legitimacy of agency organizations and their users and to reject or terminate accounts that do not meet these requirements. Accounts that appear to be spam, incomplete, or not associated with a genuine individual or agency will be rejected without notice.

---

## 3. Account, Organizations, and Workspaces

### 3.1 Organizations and Workspaces

Every user receives a personal organization with one default workspace; neither can be deleted or archived. Agency organizations are managed by an **owner**, who may invite additional members with the roles **manager** or **standard**. The Organization is responsible for the actions of all its members.

Workspaces are the collaboration primitive: any workspace member can add and remove properties, set stages, edit workspace data and custom fields, create calendar events, and invite others. Notes written in a shared workspace are visible to all of its members. Organization-scoped data (internal notes, corrected addresses, internal prices) is never visible in workspaces, not even in ones the organization created.

### 3.2 Account Security

You are responsible for:

- Keeping your login credentials confidential
- All activity that occurs under your account
- Keeping contact details current
- Notifying us immediately at **{{contactEmail}}** if you suspect unauthorized access to your account

You must not share your account with any other person.

### 3.3 Multi-Organization Users

A user may belong to multiple organizations and workspaces. Data is strictly segregated between organizations, and each organization can only access its own data.

---

## 4. Fees and Plans

The Service is **currently provided free of charge**.

We may introduce paid plans and paid features later — for example, per-seat pricing for agency organizations or higher usage limits. Our current intention is that core personal property tracking (tracking, change alerts, the personal organization and its workspace, stages, viewings) remains available free of charge. When paid plans are introduced or prices change, this will be announced at least 30 days in advance, and any applicable prices will be published before you commit to a purchase.

**All prices are exclusive of VAT**, where applicable. VAT is added at the applicable Bulgarian rate (currently 20%). The Provider is VAT-registered under VAT No. {{vatNumber}}. Price changes do not affect any already prepaid period.

---

## 5. Permitted Use and Customer Obligations

### 5.1 Permitted Use

You may use the Service only for lawful purposes and in accordance with these Terms. You agree to:

- Use the Service lawfully and only to track properties and collaborate on them
- Enter only data you are entitled to process under applicable law
- If you are an agency organization: fulfill your obligations as **data controller** towards your members and the clients you invite to workspaces (providing information, establishing a lawful basis, responding to data subject rights) — see Section 8

### 5.2 Prohibited Conduct

You agree not to:

- Use the Service for any purpose that is unlawful or prohibited by these Terms
- Submit false, misleading, or fraudulent information
- Attempt to gain unauthorized access to any part of the Service or its infrastructure
- Interfere with or disrupt the integrity or performance of the Service
- Upload files containing malware, viruses, or any malicious code
- Use the Service to transmit unsolicited communications or spam
- Reverse engineer, decompile, or attempt to extract the source code of the Service
- Reproduce, redistribute, resell, or sublicense any part of the Service without our prior written consent
- Attempt automated scraping or data extraction from the Service
- Mass-download or redistribute content (such as listing photographs and descriptions) retrieved from source sites through the Service

### 5.3 Account Suspension

In case of material breach of these Terms, we may restrict or suspend access after providing a cure notice and a reasonable period to remedy the breach. For severe breaches (abuse, security risk, fraud), we may suspend access immediately without prior notice.

---

## 6. Service Availability

### 6.1 Availability and Maintenance

We aim to keep the Service available and operational. We may perform planned maintenance, which will be announced at least 48 hours in advance where possible and carried out outside peak hours where feasible. We do not guarantee that the Service will be uninterrupted or error-free.

### 6.2 Service Changes

We reserve the right to modify, evolve, or discontinue individual features of the Service at any time. A material reduction of paid functionality within a prepaid period entitles the Customer to a pro-rata refund or period extension.

---

## 7. Intellectual Property

### 7.1 Provider's Intellectual Property

All content, design, code, trademarks, logos, and materials on the Service are the exclusive property of {{legalName}} or its licensors and are protected by applicable intellectual property laws.

You are granted a limited, non-exclusive, non-transferable license to access and use the Service solely for its intended purpose as described in these Terms, for the duration of the contract. Nothing in these Terms transfers any ownership rights to you.

### 7.2 Customer Data

**Data entered by users and Organizations (including manually entered properties, notes, custom fields, stages, and uploaded photographs) remains the entering user's or Organization's property and responsibility.** The Provider acquires no rights in it beyond the limited right to process it to deliver the Service under the [Data Processing Agreement (DPA)]({{dpaUrl}}).

Files you upload to the Service (such as photographs and floor plans) remain your property. By uploading them, you grant us a limited license to store and process them solely for the purpose of providing the Service to you.

**Listing content originates from third parties.** Photographs and text captured from listing websites belong to their respective owners (source sites and advertisers). They are retrieved and displayed solely for the purpose of tracking the property on the Service, and you may not download or redistribute them outside the Service.

---

## 8. Data Protection

### 8.1 Data Controller and Data Processor Roles

For personal data that Organizations control (their members, the clients they invite to workspaces, and the collaboration content they create), **the Organization is the data controller and the Provider is the data processor**. This relationship is governed by the [Data Processing Agreement (DPA)]({{dpaUrl}}), which forms an integral part of these Terms.

### 8.2 Provider as Data Controller

For users' own account data, authentication data, technical logs, analytics, and communications with us, the Provider acts as data controller. This processing is described in our [Privacy Policy]({{ppUrl}}).

### 8.3 Sub-Processors

The current list of sub-processors is published in the annex to the [Data Processing Agreement (DPA)]({{dpaUrl}}).

---

## 9. Confidentiality

Each party shall keep confidential the other party's non-public commercial and technical information obtained in connection with the contract, during its term and for 3 years thereafter.

---

## 10. Responsibilities and Limitations

### 10.1 Platform Disclaimer

The Service is provided on an **"as is"** and **"as available"** basis. To the fullest extent permitted by law, we disclaim all warranties, express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, and non-infringement.

We do not warrant that:

- The Service will be uninterrupted, error-free, or free of viruses
- Any errors in the Service will be corrected
- The results obtained from using the Service will be accurate or reliable
- A particular source site will remain supported, available, or permitted to be collected from

### 10.2 Data Backup Responsibility

The Service is designed to archive rather than delete user content. On request, we provide an export of the Customer's data in a commonly used, machine-readable format (see Section 13.4). The Customer is nevertheless responsible for keeping its own copies of information that is critical to it.

### 10.3 Property Data Accuracy

The Service is a tracking and collaboration tool. Listing and property data is collected from third-party sources and may be inaccurate, incomplete, or outdated. All decisions regarding a property — including purchase, rental, or negotiation decisions — are solely the responsibility of the user. We are not a party to any property transaction and provide no brokerage, escrow, or payment-processing services.

---

## 11. Limitation of Liability

### 11.1 Liability Standard

The Provider is liable only for damage caused intentionally or by gross negligence, in accordance with Bulgarian law (Obligations and Contracts Act, Article 94).

### 11.2 Liability Cap

The Provider's aggregate liability for all claims arising under or in connection with these Terms in any contract year is capped at **the amounts paid by the Customer in the preceding 12 months**. Where no amounts were paid in the preceding 12 months (for example, while the Service is free of charge), the aggregate liability is capped at the minimum amount permitted by applicable mandatory law.

### 11.3 Excluded Damages

To the fullest extent permitted by applicable law, {{legalName}} shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to:

- Loss of profits, revenue, or business opportunities
- Loss of data (except where caused by the Provider's intentional act or gross negligence)
- Damages from decisions or actions taken on the basis of property or listing data in the Service (including purchase, rental, or negotiation decisions)
- Damage caused by the Customer's actions or omissions

---

## 12. Force Majeure

Neither party is liable for non-performance caused by force majeure events, including but not limited to: natural disasters, war, terrorism, strikes, government actions, and failures of third-party infrastructure providers (including source sites blocking or degrading collection) beyond the Provider's reasonable control (in accordance with Article 306 of the Bulgarian Commerce Act).

---

## 13. Term and Termination

### 13.1 Contract Duration

The contract is concluded for an indefinite period and runs while the Customer's account is active. Deleting the account ends the contract for the Customer and the organizations it owns.

### 13.2 Termination by Customer

The Customer may terminate at any time by deleting their account through the **Delete Account** section in account settings. Amounts already prepaid for a future period are non-refundable except as provided in Section 6.2.

### 13.3 Termination by Provider

We reserve the right to suspend or permanently terminate your account, with or without notice, if:

- You breach any provision of these Terms
- Your account is found not to belong to a genuine individual or legally registered agency
- Your account appears to be used for fraudulent, abusive, or unlawful purposes
- We are required to do so by applicable law or a competent authority

### 13.4 Effect of Termination

Upon termination, the [Data Processing Agreement (DPA)]({{dpaUrl}})'s rules on return and deletion of data apply. We will provide data export upon request and will delete or anonymize data on the Organization's instruction, in accordance with the DPA. Deleting an account archives the personal organization and any agency organizations the user owns; content the user authored in other people's workspaces remains in place, attributed to a deleted user.

---

## 14. Changes to These Terms

We may update these Terms from time to time with at least **30 days' notice** by email and/or in-app notification. When we do, we will update the effective date at the top of this document.

If you do not accept the amendments, you may terminate your account before they take effect. Your continued use of the Service after updated Terms take effect constitutes your acceptance of the revised Terms.

---

## 15. Notices

Official notices are sent to **{{contactEmail}}** (to us) and to the account owner's email address (to the Customer). Notices are deemed received on the first business day after sending.

---

## 16. Governing Law and Disputes

These Terms are governed by and construed in accordance with the laws of the **Republic of Bulgaria** and applicable European Union regulations.

Any dispute arising out of or in connection with these Terms shall be subject to the exclusive jurisdiction of the competent courts of **Sofia, Bulgaria** (the Provider's registered seat).

---

## 17. Language

These Terms are drawn up in Bulgarian and English. **In case of discrepancy, the Bulgarian text prevails.**

---

## 18. Contact Us

If you have any questions about these Terms, please contact us:

**{{legalName}}**  
{{registeredAddress}}  
Email: {{contactEmail}}
